import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { emailPreviewQuery, type EmailPreviewRequest, type SavedReport } from './reportsStore'
import type { Locale } from './types'

/**
 * "CUM VA ARATA EMAILUL": chiar emailul, desenat de server cu acelasi
 * `ReportEmail` cu care il trimite cronul din ph4
 * (`POST /api/v2/reports/schedules/preview-email`), pe blocurile raportului
 * citite cu dreptul omului. Se arata intr-un iframe fara scripturi (`srcdoc`),
 * la latimea unui client de email (760) sau a unui telefon (375), micsorat
 * cat sa intre in coloana.
 *
 * Se cere din nou cand se schimba ce schimba emailul (randuri, limba,
 * atasamente, frecventa); subiectul si mesajul asteapta sa se termine scrisul.
 */

const WIDTHS = { desktop: 760, phone: 375 } as const
type Device = keyof typeof WIDTHS

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return v
}

export function EmailPreview({ open, report, request, recipients, fallbackSubject, locale }: {
  open: boolean
  report: SavedReport
  request: Omit<EmailPreviewRequest, 'company_id' | 'name' | 'blocks'>
  recipients: string[]
  /** Subiectul scris in ecran, cat timp serverul n-a raspuns. */
  fallbackSubject: string
  locale: Locale
}) {
  const t = (ro: string, en: string) => (locale === 'ro' ? ro : en)
  const [device, setDevice] = useState<Device>('desktop')
  const typed = useDebounced({ subject: request.subject, message: request.message }, 400)
  const body: EmailPreviewRequest = {
    company_id: report.company_id,
    name: report.name,
    blocks: report.blocks.map((b) => ({ title: b.title, definition: b.definition })),
    ...request,
    ...typed,
  }
  const preview = useQuery({ ...emailPreviewQuery(body), enabled: open && report.company_id > 0, placeholderData: keepPreviousData })

  const box = useRef<HTMLDivElement>(null)
  const frame = useRef<HTMLIFrameElement>(null)
  const [room, setRoom] = useState(0)
  const [height, setHeight] = useState(640)
  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const measure = () => setRoom(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const width = WIDTHS[device]
  const scale = room > 0 ? Math.min(1, room / width) : 1
  const fit = () => {
    try {
      const doc = frame.current?.contentDocument
      const h = doc?.documentElement?.scrollHeight ?? 0
      if (h > 0) setHeight(h)
    } catch {
      /* fara acces la document: ramane inaltimea de pana acum */
    }
  }

  const data = preview.data
  /* Alta latime = alta inaltime a emailului; onLoad nu mai vine, deci se masoara aici. */
  useEffect(() => {
    if (typeof requestAnimationFrame === 'undefined') return
    const id = requestAnimationFrame(fit)
    return () => cancelAnimationFrame(id)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [device, data?.html])

  return (
    <div className="od-rep-sched-preview">
      <div className="od-rep-preview-bar">
        <span className="od-rep-form-label">{t('Cum va arata emailul', 'How the email will look')}</span>
        <div className="btn-group btn-group-xs od-rep-device" role="group" aria-label={t('Latimea ecranului', 'Screen width')}>
          {(['desktop', 'phone'] as Device[]).map((d) => (
            <button key={d} type="button" className={`btn btn-default${device === d ? ' active' : ''}`} aria-pressed={device === d} onClick={() => setDevice(d)}>
              {d === 'desktop' ? t('Calculator', 'Desktop') : t('Telefon', 'Phone')} <span className="od-rep-meta">{WIDTHS[d]}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="od-rep-mail">
        <dl className="od-rep-mail-head">
          <dt>{t('De la', 'From')}</dt>
          <dd>OptimumDesk Reports</dd>
          <dt>{t('Catre', 'To')}</dt>
          <dd>{recipients.length > 0 ? recipients.join(', ') : <span className="od-rep-meta">{t('(niciun destinatar)', '(no recipients yet)')}</span>}</dd>
          <dt>{t('Subiect', 'Subject')}</dt>
          <dd className="od-rep-mail-subject">{data?.subject ?? fallbackSubject}</dd>
        </dl>
        <div ref={box} className="od-rep-mail-frame" data-device={device} style={{ height: Math.ceil(height * scale) }}>
          {data ? (
            <iframe
              ref={frame}
              title={t('Previzualizarea emailului', 'Email preview')}
              className="od-rep-mail-iframe"
              sandbox="allow-same-origin"
              srcDoc={data.html}
              onLoad={fit}
              style={{ width, height, transform: scale < 1 ? `scale(${scale})` : undefined }}
            />
          ) : preview.isError ? (
            <p className="od-rep-mail-error" role="alert">
              {t('Previzualizarea nu s-a putut face acum. Emailul pleaca oricum la ora programata.', 'The preview could not be built right now. The email still goes out on schedule.')}
            </p>
          ) : (
            <p className="od-rep-meta od-rep-mail-loading">{t('Se pregateste previzualizarea...', 'Building the preview...')}</p>
          )}
        </div>
      </div>
      {data?.sample ? (
        <p className="od-rep-meta od-rep-mail-sample">
          {t(
            'Previzualizarea citeste cel mult 50 de randuri pe bloc; in email numarul de randuri, barele si ponderile sunt pe tot raportul.',
            'The preview reads at most 50 rows per block; in the email the row count, bars and shares cover the whole report.'
          )}
        </p>
      ) : null}
    </div>
  )
}
